import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { MaskLine, Overline } from "@/components/Reveal";
import { ASSETS } from "@/lib/constants";

const FloatJar = ({ src, className, alt, delay = 0, dur = 6, drift = -18, rot = 2, y }) => (
  <motion.div className={`absolute ${className}`} style={{ y }}>
    <motion.img
      src={src} alt={alt} loading="eager"
      animate={{ y: [0, drift, 0], rotate: [0, rot, -rot, 0] }}
      transition={{ duration: dur, delay, repeat: Infinity, ease: "easeInOut" }}
      className="w-full product-shadow"
    />
  </motion.div>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBack = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yMid = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative grain min-h-[100svh] overflow-hidden bg-forest-deep pt-24"
      data-testid="hero-section">
      <video autoPlay loop muted playsInline data-testid="hero-video"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-90">
        <source src="/media/model-beetroot.mp4" type="video/mp4" />
      </video>
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-forest-deep via-forest-deep/80 to-transparent" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/45" />
      <div className="absolute inset-0 spotlight" />
      <div className="pointer-events-none absolute inset-0 opacity-40"
        style={{ background: "radial-gradient(ellipse at 70% 20%, rgba(255,87,34,0.18), transparent 45%), radial-gradient(ellipse at 20% 80%, rgba(211,47,47,0.14), transparent 45%)" }} />

      <motion.div style={{ y: yText, opacity }}
        className="relative z-20 mx-auto flex min-h-[80svh] max-w-7xl flex-col justify-center px-5 lg:px-8">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="mb-6 flex items-center gap-2">
          <span className="flex items-center gap-2 rounded-full glass px-4 py-1.5">
            <Sparkles size={14} className="text-gold" />
            <Overline>Snack Smart. Live Bold.</Overline>
          </span>
        </motion.div>

        <h1 className="font-display text-[15vw] leading-[0.86] tracking-tight text-cream sm:text-[12vw] lg:text-[9vw]">
          <MaskLine delay={0.25}>BIG CRUNCH.</MaskLine>
          <MaskLine delay={0.4} className="text-gold-gradient">BOLD FLAVOUR.</MaskLine>
        </h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-7 max-w-xl text-base text-white/60 sm:text-lg">
          Protein-rich snacking crafted for the modern lifestyle. Real soya, bold Indian
          flavours, baked then lightly fried for the perfect crunch.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center gap-4">
          <Link to="/shop" data-testid="hero-shop-now"
            className="group flex items-center gap-2 rounded-full gold-gradient px-8 py-4 font-oswald text-sm uppercase tracking-[0.15em] text-black transition-transform hover:scale-105">
            Shop Now <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <a href="#flavours" data-testid="hero-explore-flavours"
            className="rounded-full border border-white/25 px-8 py-4 font-oswald text-sm uppercase tracking-[0.15em] text-cream transition-colors hover:border-gold hover:text-gold">
            Explore Flavours
          </a>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
          className="mt-12 flex flex-wrap gap-x-8 gap-y-3">
          {["8g Protein", "Rich in Iron", "100% Veg", "No Preservatives"].map((s) => (
            <span key={s} className="font-oswald text-xs uppercase tracking-[0.2em] text-white/45">• {s}</span>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
